/**
 * =============================================================================
 * PageHeader — B-6 shared primitive (ADR-025 §6.2; design-v3 §F)
 * =============================================================================
 *
 * EXTRACTED from the queue/media panel-header idiom:
 *
 *   Box(padding md[, paddingBottom]) > Flex(space-between, center) >
 *     FlexItem{heading} + [FlexItem{action}]  > {children}
 *
 * The heading is passed AS A NODE so its level/id/tabIndex stay caller-owned
 * (the AdminShell section H1s — the M8 focus targets — and the workspace's
 * focus-managed <h2> are deliberately NOT consumers; design-v3 §F). The action
 * slot likewise stays caller-owned JSX so refs (`refreshBtnRef` is a
 * load-bearing focus anchor for three dialog flows) and testids are untouched;
 * pass null/undefined to omit the action FlexItem entirely (the media header
 * renders its Refresh only after first load).
 * =============================================================================
 */

import React from 'react';
import { Box, Flex, FlexItem } from '@bigcommerce/big-design';

export interface PageHeaderProps {
  /** Heading node (e.g. <H2 marginBottom="none">…</H2>) — caller-owned. */
  heading: React.ReactNode;
  /** Right-aligned action node; when null/undefined its FlexItem is not rendered. */
  action?: React.ReactNode;
  /** Forwarded to the Box (the media header uses paddingBottom="none"). */
  paddingBottom?: 'none';
  /** Content after the heading row inside the same Box (e.g. a description). */
  children?: React.ReactNode;
}

export function PageHeader({
  heading,
  action,
  paddingBottom,
  children,
}: PageHeaderProps): React.ReactElement {
  return (
    <Box padding="medium" paddingBottom={paddingBottom}>
      <Flex justifyContent="space-between" alignItems="center">
        <FlexItem>{heading}</FlexItem>
        {action != null && <FlexItem>{action}</FlexItem>}
      </Flex>
      {children}
    </Box>
  );
}
